import { useEffect, useState } from "react";
import {
  EmptyState,
  EventFlyer,
  EventMeta,
  HostedBy,
  PublicPageIntro,
  SaleStatusBadge
} from "../components/public-marketplace";
import { listPublicEvents } from "../lib/api";
import { formatTicketPrice, isTierPurchasableNow } from "../lib/public-marketplace";
import type { Event } from "../lib/types";

interface EventsPageProps {
  navigate: (path: string) => void;
  mode?: "current" | "previous";
  initialEvents?: Event[];
}

export function splitEventsByTime(events: Event[], now = Date.now()) {
  const current: Event[] = [];
  const previous: Event[] = [];

  for (const event of events) {
    const startsAt = new Date(event.startsAt).getTime();
    if (Number.isNaN(startsAt) || startsAt >= now) current.push(event);
    else previous.push(event);
  }

  current.sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime());
  previous.sort((left, right) => new Date(right.startsAt).getTime() - new Date(left.startsAt).getTime());
  return { current, previous };
}

export function getPreviousEventsCtaPlacement(mode: "current" | "previous", currentEventCount: number) {
  if (mode !== "current") return "none" as const;
  return currentEventCount > 0 ? ("bottom" as const) : ("empty" as const);
}

export function getEventListingCta(event: Event, now = Date.now()) {
  if (event.salesStatus === "sold_out") return { label: "View Event", available: false, status: "Sold Out" };
  if (event.salesStatus === "sales_paused" || event.status !== "live") return { label: "View Event", available: false, status: "Sales Paused" };

  const tiers = event.ticketTiers ?? [];
  if (tiers.some((tier) => isTierPurchasableNow(event, tier, now))) {
    return { label: "Get Tickets Now", available: true, status: "On Sale" };
  }

  const allSoldOut = tiers.length > 0 && tiers.every((tier) =>
    tier.status === "sold_out" || tier.salesStatus === "sold_out" ||
    tier.inventoryLimit === 0 || (tier.saleEndsAt !== undefined && Date.parse(tier.saleEndsAt) <= now)
  );
  if (allSoldOut) return { label: "View Event", available: false, status: "Sold Out" };

  const allPaused = tiers.length > 0 && tiers.every((tier) =>
    tier.status === "paused" || tier.status === "hidden" || tier.salesStatus === "sales_paused"
  );
  if (allPaused) return { label: "View Event", available: false, status: "Sales Paused" };

  return { label: "View Event", available: false, status: "Tickets Unavailable" };
}

export function filterEvents(events: Event[], filters: { q?: string; date?: string; location?: string }, now = new Date()) {
  const query = filters.q?.trim().toLowerCase() ?? "";
  const location = filters.location && filters.location !== "all" ? filters.location.toLowerCase() : "";
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);
  const endOfWeek = new Date(startOfToday);
  endOfWeek.setDate(endOfWeek.getDate() + 7);

  return events.filter((event) => {
    const haystack = [event.title, event.description, event.venueName, event.city, event.region].filter(Boolean).join(" ").toLowerCase();
    if (query && !haystack.includes(query)) return false;
    if (location && ![event.city, event.venueName, event.region].filter(Boolean).join(" ").toLowerCase().includes(location)) return false;

    const startsAt = new Date(event.startsAt);
    if (filters.date === "today") return startsAt >= startOfToday && startsAt < endOfToday;
    if (filters.date === "week") return startsAt >= startOfToday && startsAt < endOfWeek;
    return true;
  });
}

export default function EventsPage({ navigate, mode = "current", initialEvents }: EventsPageProps) {
  const [events, setEvents] = useState<Event[]>(initialEvents ?? []);
  const [isLoading, setIsLoading] = useState(!initialEvents);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (initialEvents) return;
    setIsLoading(true);
    setHasError(false);
    listPublicEvents(mode)
      .then(setEvents)
      .catch(() => {
        setEvents([]);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [initialEvents, mode]);

  const isPrevious = mode === "previous";
  const queryParams = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  const filteredEvents = filterEvents(events, {
    q: queryParams.get("q") ?? undefined,
    date: queryParams.get("date") ?? undefined,
    location: queryParams.get("location") ?? undefined,
  });
  const split = initialEvents ? splitEventsByTime(filteredEvents) : undefined;
  const visibleEvents = split ? (isPrevious ? split.previous : split.current) : filteredEvents;
  const ctaPlacement = getPreviousEventsCtaPlacement(mode, split ? split.current.length : visibleEvents.length);

  return (
    <section className="page marketplace-page">
      <PublicPageIntro
        eyebrow={isPrevious ? "Previous Events" : "AnTix Events"}
        title={isPrevious ? "Previous Events" : "Tickets"}
        description={
          isPrevious
            ? "A look back at nights that already ran through AnTix."
            : "Discover upcoming events and secure your spot."
        }
        actions={
          <button
            className="marketplace-link-button"
            type="button"
            onClick={() => navigate(isPrevious ? "/tickets" : "/tickets/previous")}
          >
            {isPrevious ? "Back to Tickets" : "View Previous Events"}
          </button>
        }
      />

      {isLoading && (
        <div className="marketplace-loading" aria-live="polite">
          {Array.from({ length: isPrevious ? 3 : 2 }).map((_, index) => (
            <article className="marketplace-event marketplace-event-skeleton" key={index}>
              <div className="marketplace-flyer marketplace-flyer-skeleton" />
              <div className="marketplace-event__body">
                <div className="marketplace-skeleton-line marketplace-skeleton-line-short" />
                <div className="marketplace-skeleton-line marketplace-skeleton-line-title" />
                <div className="marketplace-skeleton-line" />
                <div className="marketplace-skeleton-line marketplace-skeleton-line-wide" />
                <div className="marketplace-tier-list">
                  <div className="tier-purchase-row tier-purchase-row-skeleton" />
                  <div className="tier-purchase-row tier-purchase-row-skeleton" />
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {!isLoading && hasError && (
        <EmptyState
          title="We couldn't load tickets right now"
          body="Please try again in a moment."
          action={
            <button className="marketplace-link-button" type="button" onClick={() => navigate(window.location.pathname)}>
              Retry
            </button>
          }
        />
      )}

      {!isLoading && !hasError && visibleEvents.length === 0 && (
        <EmptyState
          title={isPrevious ? "No previous events yet" : "No upcoming events right now"}
          body={isPrevious ? "Past nights will appear here once events are completed." : "Check back soon for the next AnTix event."}
          action={
            ctaPlacement === "empty" ? (
              <button className="marketplace-link-button" type="button" onClick={() => navigate("/tickets/previous")}>
                Browse Previous Events
              </button>
            ) : undefined
          }
        />
      )}

      {!isLoading && !hasError && visibleEvents.length > 0 && (
        <>
          {isPrevious ? (
            <div className="marketplace-previous-grid">
              {visibleEvents.map((event) => (
                <article className="marketplace-previous-card" key={event.id}>
                  <EventFlyer event={event} compact />
                  <div className="marketplace-previous-card__body">
                    <SaleStatusBadge event={event} isPrevious />
                    <h2>{event.title}</h2>
                    <EventMeta event={event} />
                    <HostedBy hostedBy={event.hostedBy} />
                    <button className="marketplace-link-button" type="button" onClick={() => navigate(`/e/${event.slug}`)}>
                      View Event
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="marketplace-event-list">
              {visibleEvents.map((event, index) => {
                const cta = getEventListingCta(event);
                return <article className="marketplace-event" key={event.id}>
                  <EventFlyer event={event} priority={index === 0} />
                  <div className="marketplace-event__body">
                    <div className="marketplace-event__topline">
                      <span className={`marketplace-status${cta.available ? " marketplace-status-live" : " marketplace-status-muted"}`}>{cta.status}</span>
                      {event.lowestAvailablePriceCents !== undefined && (
                        <span className="marketplace-price-kicker">From {formatTicketPrice(event.lowestAvailablePriceCents)}</span>
                      )}
                    </div>
                    <div className="marketplace-event__heading">
                      <div>
                        <h2>{event.title}</h2>
                        <HostedBy hostedBy={event.hostedBy} />
                      </div>
                    </div>
                    <EventMeta event={event} />
                    {event.description && <p className="marketplace-event__description">{event.description}</p>}
                    <button
                      className={cta.available ? "primary-action" : "marketplace-link-button"}
                      type="button"
                      onClick={() => navigate(`/e/${event.slug}`)}
                      aria-label={`${cta.label} for ${event.title}`}
                    >
                      {cta.label}
                    </button>
                  </div>
                </article>;
              })}
            </div>
          )}

          {ctaPlacement === "bottom" && (
            <div className="marketplace-secondary-cta">
              <button className="marketplace-link-button" type="button" onClick={() => navigate("/tickets/previous")}>
                View Previous Events
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
